import React, { useState, useEffect, useCallback } from "react";
import "./BasicClaimInfoForm.css";
import { classNames } from "primereact/utils";
import { InputText } from "primereact/inputtext";
import { Field, Form } from "react-final-form";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { getLenders } from "../../services/lenders";
import { AutoComplete } from "primereact/autocomplete";
import {
  fetchAccountNumberOrSSN,
  getDuplicateClaims
} from "../../services/packets";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { camelToTitle } from "../../utils";
import { getVendorConnectedCompanies } from "../../services/vendors";
import { getCarrierLogoImage } from "../../services/otp";

const financeTypeOptions = [
  { label: "Lease", value: "Lease" },
  { label: "Retail", value: "Retail" },
  { label: "Owned", value: "Owned" }
];

const BasicClaimInfoForm = ({
  basicClaimInfo,
  basicClaimInfoSubmit,
  requiredFields,
  stepperRef,
  existingPacket,
  carrier,
  user,
  lender,
  setLender,
  financed,
  setFinanced = () => {},
  mode,
  changeMode,
  testing
}) => {
  const [lenderSearchResults, setLenderSearchResults] = useState([]);
  const [initialValues, setInitialValues] = useState({});
  const [trueRequiredFields, setTrueRequiredFields] = useState(requiredFields);
  const [lenderTooltip, setLenderTooltip] = useState("");
  const [isLenderSelected, setIsLenderSelected] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [vendorCarriers, setVendorCarriers] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  const validate = data => {
    let errors = {};
    trueRequiredFields.forEach(field => {
      if (!data[field]) {
        errors[field] = `${camelToTitle(field)} is required.`;
      }
    });
    if (data?.vin?.length !== 17 && data?.vin?.length !== 12) {
      errors["vin"] = "VIN or HIN is invalid";
    }
    return errors;
  };

  useEffect(() => {
    if (!carrier?.thirdPartyAdministrator) {
      setSelectedCarrier(basicClaimInfo.carrierId || carrier?.carrierId);
    }
  }, [carrier, basicClaimInfo]);

  // grab all carriers associated with the vendor IF carrier.vendorAuthority
  useEffect(() => {
    const updateVendorCompanies = async () => {
      if (carrier?.thirdPartyAdministrator) {
        const carriers = await getVendorConnectedCompanies();
        const userCarrier = user?.requestingCompanyId;
        const purgedCarriers = carriers.filter(
          c => c.carrierId !== userCarrier
        );
        if (!purgedCarriers || purgedCarriers.length <= 1) {
          setVendorCarriers([]);
        }
        const carrierOptions = [];
        for (const c of purgedCarriers) {
          const logo = await getCarrierLogoImage(c.carrierId);
          carrierOptions.push({
            label: c.name,
            value: c.carrierId,
            logo: logo
          });
        }
        if (vendorCarriers?.length !== carrierOptions?.length) {
          setVendorCarriers(carrierOptions);
          setSelectedCarrier(carrierOptions[0].value);
        }
      }
    };
    updateVendorCompanies();
  }, [carrier, vendorCarriers, user]);

  // handle lender tooltip based on selected lender
  useEffect(() => {
    if (lender?.lenderName && lender?.value !== "NEW" && !lender.alias) {
      setLenderTooltip("");
      return;
    }
    if (!lender?.lenderName) {
      setLenderTooltip(`If the lender is not found, you can add a new lender.
        Adding a new lender will delay processing until the lender can be verified.`);
      return;
    }
    if (lender.value === "NEW") {
      setLenderTooltip(
        `Adding a new lender will delay processing until the lender can be verified.`
      );
      return;
    }
    if (lender.alias) {
      setLenderTooltip(`This lender is also known as ${lender.alias}`);
      return;
    }
  }, [lender]);

  // remove account number and lender from trueRequiredFields if finance type is Owned
  useEffect(() => {
    if (
      !financed &&
      (trueRequiredFields.includes("accountNumber") ||
        trueRequiredFields.includes("lender"))
    ) {
      setTrueRequiredFields(
        trueRequiredFields.filter(
          field => field !== "accountNumber" && field !== "lender"
        )
      );
    } else {
      if (financed && !trueRequiredFields.includes("accountNumber")) {
        setTrueRequiredFields([...trueRequiredFields, "accountNumber"]);
      }
      if (financed && !trueRequiredFields.includes("lender")) {
        setTrueRequiredFields([...trueRequiredFields, "lender"]);
      }
    }
  }, [financed, trueRequiredFields]);

  // prepopulate form with existing packet data when editing
  // or with basicClaimInfo when creating and moving back to this step
  useEffect(() => {
    if (existingPacket?.packetId) {
      fetchAccountNumberOrSSN(existingPacket.packetId).then(
        response => (existingPacket.accountNumber = response.accountNumberOrSSN)
      );
      if (existingPacket?.insurerType === "Third Party") {
        existingPacket.thirdPartyClaim = true;
      }
      setInitialValues(existingPacket);
    } else {
      if (basicClaimInfo?.insurerType === "Third Party") {
        basicClaimInfo.thirdPartyClaim = true;
      }
      setInitialValues(basicClaimInfo);
    }
  }, [existingPacket, basicClaimInfo]);

  // populate form with test data
  const devPopulate = async data => {
    data.change("accountNumber", "123456789");
    data.change("claimNumber", `test${Math.floor(Math.random() * 1000000)}`);
    data.change("vin", "11111111111111111");
    data.change("financeType", "Lease");
    setFinanced(true);
    const lenders = await getLenders("test");
    data.change("lender", lenders[0]);
    setLender(lenders[0]);
    data.change("socialSecurityNumber", "1234");
  };

  // checks if claim number already exists and shows warning if it does
  const checkForDuplicates = async claimNumber => {
    if (mode !== "create") {
      return false;
    }
    if (claimNumber) {
      const claims = await getDuplicateClaims(claimNumber);
      return claims.duplicateClaims.length !== 0;
    }
  };

  const resetDuplicateWarning = () => {
    setShowDuplicateWarning(false);
  };

  const searchLenders = event => {
    const query = event.query;
    getLenders(query)
      .then(data => {
        const res = data.map(lender => {
          return {
            label: lender.lenderName,
            value: lender.lenderId,
            ...lender
          };
        });
        if (
          !res.some(
            lender => lender.lenderName.toLowerCase() === query.toLowerCase()
          )
        ) {
          // add a new lender option if an exact match is not found
          res.push({
            lenderName: `Add ${query}`,
            label: query,
            value: "NEW",
            lenderId: null
          });
        }
        setLenderSearchResults(res);
      })
      .catch(() => setLenderSearchResults([]));
  };

  const saveClaimInfo = async form => {
    const errors = validate(form);
    if (!Object.keys(errors).length) {
      // check for duplicates
      const duplicates = await checkForDuplicates(form.claimNumber);
      if (duplicates && !showDuplicateWarning) {
        setShowDuplicateWarning(true);
        return;
      }

      form.insurerType = form?.thirdPartyClaim ? "Third Party" : "First Party";
      delete form.thirdPartyClaim;

      form.carrierId = selectedCarrier;

      basicClaimInfoSubmit(form);
      stepperRef.current.nextCallback();
    }
  };

  // format results in lender dropdown
  const lenderSearchTemplate = lender => {
    if (lender.value.includes("NEW")) {
      return (
        <span className={"NewLender"}>
          <i className="pi pi-plus"></i>
          <span className={"NewLenderName"}>{lender.lenderName}</span>
        </span>
      );
    }
    if (lender.routeOneId || lender.dtOriginId) {
      return (
        <span>
          <i className="fas fa-tachometer-alt" />
          <span className={"LenderSearchDropDown"}> {lender.lenderName}</span>
        </span>
      );
    }
    return <span className={"LenderSearchDropDown"}>{lender.lenderName}</span>;
  };

  // format selected lender
  const selectedItemTemplate = item => {
    if (item.value !== "NEW" && item.lenderName) {
      return `${item.lenderName}`;
    }
    if (item.value === "NEW") {
      return `+ ${item.lenderName.slice(4)}`;
    }
  };

  const isFormFieldValid = meta => !!(meta.touched && meta.error);

  const getFormErrorMessage = meta => {
    return (
      isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>
    );
  };

  const companyTemplate = useCallback(option => {
    return (
      <div className="CompanyOption">
        <img
          alt={option?.label}
          src={option?.logo}
          style={{
            display: "inline-block",
            margin: "5px 0 2px 5px",
            width: "36px"
          }}
        />
        <span style={{ fontSize: "1rem" }}>{option?.label}</span>
      </div>
    );
  }, []);

  return (
    <div className={"ClaimInfoForm"}>
      <div className={"FormField"}>
        <Form
          onSubmit={saveClaimInfo}
          initialValues={initialValues ?? basicClaimInfo}
          validate={validate}
          render={({ handleSubmit, form }) => (
            <form onSubmit={handleSubmit} className="p-fluid FormWrapper">
              {mode === "create" &&
                carrier?.thirdPartyAdministrator &&
                vendorCarriers &&
                vendorCarriers?.length > 0 && (
                  <Field
                    name="carrierId"
                    required
                    render={({ input, meta }) => (
                      <div className="field">
                        <span className="p-float-label">
                          <Dropdown
                            id={input.name}
                            {...input}
                            className={classNames({
                              "p-invalid": isFormFieldValid(meta)
                            })}
                            options={vendorCarriers}
                            optionLabel={"label"}
                            itemTemplate={companyTemplate}
                            valueTemplate={companyTemplate}
                            value={
                              vendorCarriers.find(
                                v => v?.value === selectedCarrier
                              )?.value
                            }
                            onChange={e => {
                              setSelectedCarrier(e.value);
                              input.onChange(e.value);
                            }}
                          />
                          <label
                            htmlFor={input.name}
                            className={classNames({
                              "p-error": isFormFieldValid(meta)
                            })}
                          >
                            Company*
                          </label>
                        </span>
                        {getFormErrorMessage(meta)}
                      </div>
                    )}
                  />
                )}
              <Field
                name="claimNumber"
                required
                render={({ input, meta }) => (
                  <div className="field">
                    <span className="p-float-label">
                      <InputText
                        id={input.name}
                        {...input}
                        className={classNames({
                          "p-invalid": isFormFieldValid(meta)
                        })}
                        keyfilter={/carrier.claimNumberValidationRegex/}
                        onInput={resetDuplicateWarning}
                        maxLength={30}
                        validateOnly
                      />
                      <label
                        htmlFor={input.name}
                        className={classNames({
                          "p-error": isFormFieldValid(meta)
                        })}
                      >
                        Claim Number*
                      </label>
                    </span>
                    {getFormErrorMessage(meta)}
                  </div>
                )}
              />
              {showDuplicateWarning && (
                <div className="DuplicateWarning">
                  Possible duplicate claim. Please verify.
                </div>
              )}
              <Field
                name="vin"
                render={({ input, meta }) => (
                  <div className="field">
                    <span className="p-float-label">
                      <InputText
                        id={input.name}
                        {...input}
                        className={classNames({
                          "p-invalid": isFormFieldValid(meta)
                        })}
                        maxLength={30}
                      />
                      <label
                        htmlFor={input.name}
                        className={classNames({
                          "p-error": isFormFieldValid(meta)
                        })}
                      >
                        VIN (or other IN)*
                      </label>
                    </span>
                    {getFormErrorMessage(meta)}
                  </div>
                )}
              />
              <Field
                name="financeType"
                render={({ input, meta }) => (
                  <div className="field">
                    <span className="p-float-label">
                      <Dropdown
                        id={input.name}
                        {...input}
                        className={classNames({
                          "p-invalid": isFormFieldValid(meta)
                        })}
                        options={financeTypeOptions}
                        optionLabel={"label"}
                        onChange={e => {
                          input.onChange(e.value);
                          setFinanced(e.value !== "Owned");
                        }}
                      />
                      <label
                        htmlFor={input.name}
                        className={classNames({
                          "p-error": isFormFieldValid(meta)
                        })}
                      >
                        Vehicle Financing*
                      </label>
                    </span>
                    {getFormErrorMessage(meta)}
                  </div>
                )}
              />
              {(financed ||
                ["Retail", "Lease"].includes(
                  form.getState().values.financeType
                )) && (
                <div>
                  <Field
                    name="accountNumber"
                    required
                    render={({ input, meta }) => (
                      <div className="field">
                        <span className="p-float-label">
                          <InputText
                            id="accountNumber"
                            {...input}
                            className={classNames({
                              "p-invalid": isFormFieldValid(meta)
                            })}
                            maxLength={30}
                          />
                          <label
                            htmlFor="accountNumber"
                            className={classNames({
                              "p-error": isFormFieldValid(meta)
                            })}
                          >
                            {carrier.ssnAllowed
                              ? "Account Number/SSN*"
                              : "Account Number*"}
                          </label>
                        </span>
                        {getFormErrorMessage(meta)}
                      </div>
                    )}
                  />
                  <Tooltip target={".lender-tooltip"} />
                  <Field
                    name="lender"
                    render={({ input, meta }) => (
                      <div
                        className="field lender-tooltip"
                        data-pr-tooltip={lenderTooltip}
                        data-pr-position="left"
                        data-pr-at="left center"
                        data-pr-my="right center"
                      >
                        <span className="p-float-label">
                          <AutoComplete
                            id={input.name}
                            value={lender}
                            onSelect={e => {
                              input.onChange(e.value);
                              setLender(e.value);
                              setIsLenderSelected(true);
                            }}
                            onKeyPress={() => {
                              setLender(null);
                              setIsLenderSelected(false);
                            }}
                            field={"lenderName"}
                            {...input}
                            dropdown
                            className={`${classNames({
                              "p-invalid": isFormFieldValid(meta)
                            })} LenderSearchDropDown`}
                            suggestions={lenderSearchResults}
                            completeMethod={searchLenders}
                            itemTemplate={lenderSearchTemplate}
                            selectedItemTemplate={selectedItemTemplate}
                            maxLength={100}
                            onBlur={() => {
                              input.onBlur();
                              // Clear the input if the value is not in the suggestions
                              if (!isLenderSelected) {
                                input.onChange("");
                                setLender(null);
                                setIsLenderSelected(false);
                              }
                            }}
                          />
                          <label
                            htmlFor={input.name}
                            className={classNames({
                              "p-error": isFormFieldValid(meta)
                            })}
                          >
                            Lender*
                          </label>
                        </span>
                        {getFormErrorMessage(meta)}
                      </div>
                    )}
                  />
                </div>
              )}
              {lender?.ssnRequired && financed && (
                <>
                  <Field
                    name="socialSecurityNumber"
                    required
                    render={({ input, meta }) => (
                      <div className="field">
                        <span className="p-float-label">
                          <InputText
                            id={input.name}
                            {...input}
                            className={classNames({
                              "p-invalid": isFormFieldValid(meta)
                            })}
                            keyfilter={/^\d{1,4}$/}
                          />
                          <label
                            htmlFor={input.name}
                            className={classNames({
                              "p-error": isFormFieldValid(meta)
                            })}
                          >
                            Last Four (SSN)
                          </label>
                        </span>
                        {getFormErrorMessage(meta)}
                      </div>
                    )}
                  />
                </>
              )}

              {financed && (
                <Field
                  name="thirdPartyClaim"
                  type={"checkbox"}
                  render={({ input, meta }) => (
                    <div className="field">
                      <span className="p-float-label">
                        <Checkbox
                          id="thirdPartyClaim"
                          {...input}
                          className={classNames({
                            "p-invalid": isFormFieldValid(meta)
                          })}
                        />
                        <label
                          htmlFor="thirdPartyClaim"
                          className={classNames({
                            "checkbox-odometer": true,
                            "p-error": isFormFieldValid(meta)
                          })}
                        >
                          Third-party Claim
                        </label>
                      </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}
                />
              )}

              {mode === "create" && (
                <div className={"ClaimNavButtonSingle"}>
                  {testing && (
                    <Button
                      label={"😎"}
                      className={"NextButton"}
                      onClick={() => devPopulate(form)}
                      type={"button"}
                    />
                  )}
                  <div className={"NextButton"} />
                  <Button
                    disabled={false}
                    label={"Select Orders"}
                    type={"submit"}
                    className={"NextButton"}
                    icon={"pi pi-arrow-right"}
                    iconPos={"right"}
                  />
                </div>
              )}

              {mode === "edit" && (
                <div className={"ClaimNavButtonSingle"}>
                  <Button
                    label={"Cancel"}
                    onClick={() => changeMode("orders")}
                    className={"NextButton"}
                    type={"button"}
                  />
                  <div className={"NextButton"} />
                  <Button
                    label={"Save and Continue"}
                    type={"submit"}
                    className={"NextButton Wide"}
                    icon={"pi pi-arrow-right"}
                    iconPos={"right"}
                  />
                </div>
              )}
            </form>
          )}
        />
      </div>
    </div>
  );
};
export default BasicClaimInfoForm;
